import { generateVerifierAndChallenge } from "~/auth/PKCEClient";
import Config from "~/config";
import logger from "~/helpers/logger";

const authUrl = Config.authUrl;
const clientId = Config.clientId;
const tenantId = Config.tenantId;

const APP_TOKENS_KEY = "appTokens";
const PKCE_VERIFIER_KEY = "pkceVerifier";
export const OAUTH_STATE_HASH_KEY = "oauth_state_hash";

/** localStorage keys cleared on logout. Add new session-bound keys here as features add them.
 *  `persist:redux` is redux-persist's key for the `redux` persist config (settings/site slices).
 *  `persist:cycleCountCache` is the cycle-count offline read cache's own nested persist config
 *  (`store/index.ts`) — tenant-scoped data that must not leak to the next login on a shared device.
 *  It gets a key of its own (rather than sharing `persist:redux`) specifically so it can be cleared
 *  here without also wiping the unrelated settings/site prefs `persist:redux` carries — this list
 *  never uses `localStorage.clear()` for exactly that reason. */
const SESSION_BOUND_LOCAL_STORAGE_KEYS = [
	APP_TOKENS_KEY,
	PKCE_VERIFIER_KEY,
	"persist:redux",
	"persist:cycleCountCache"
];

/** FusionAuth `/oauth2/token` response (PKCE authorization-code exchange). */
interface OAuthTokenResponse {
	access_token: string;
	id_token?: string;
	refresh_token: string;
}

/** FusionAuth `/api/jwt/refresh` response. */
interface JwtRefreshApiResponse {
	token: string;
	refreshToken: string;
}

/** Hash a string with SHA-256 and return a hex digest (used for CSRF state validation). */
export const sha256Hex = async (input: string): Promise<string> => {
	const data = new TextEncoder().encode(input);
	const hashBuffer = await crypto.subtle.digest("SHA-256", data);
	return Array.from(new Uint8Array(hashBuffer))
		.map((b) => b.toString(16).padStart(2, "0"))
		.join("");
};

/**
 * Sanitize a returnTo path: only relative paths starting with a single "/" are allowed.
 * Rejects absolute URLs, protocol-relative (`//host`) URLs, and anything else — returns "/".
 */
export const sanitizeReturnTo = (returnTo: unknown): string => {
	if (typeof returnTo !== "string" || !returnTo) {
		return "/";
	}
	// Reject protocol-relative (//host) and absolute (scheme:) URLs.
	if (returnTo.startsWith("//") || /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(returnTo)) {
		return "/";
	}
	if (!returnTo.startsWith("/")) {
		return "/";
	}
	return returnTo;
};

/** Protected localStorage write — swallows QuotaExceededError so a full store never crashes auth. */
const safeSetItem = (key: string, value: string): void => {
	try {
		window.localStorage.setItem(key, value);
	} catch {
		// QuotaExceededError — storage full, skip the write.
	}
};

/**
 * SFAuth — the single owner of FusionAuth PKCE tokens for the app.
 *
 * PKCE-only (authorization code + PKCE, no implicit, no client secret, no cookie/session-manager
 * path). Tokens live in memory mirrored to localStorage (`appTokens`); the access token is
 * transparently refreshed near expiry. This is the only module that reads/writes tokens — the RTK
 * base query and AuthProvider go through `checkSession()` / `getTokens()`, never localStorage.
 */
export class SFAuth {
	private static _instance: SFAuth;

	private currentAccessToken: Maybe<string> = null;
	private currentIdToken: Maybe<string> = null;
	private currentRefreshToken: Maybe<string> = null;

	private constructor() {}

	public static get Instance(): SFAuth {
		if (!SFAuth._instance) {
			SFAuth._instance = new SFAuth();
		}
		return SFAuth._instance;
	}

	/**
	 * Resolve the current access token, refreshing if near expiry (or when `forceRenewToken`).
	 * Returns "" when there is no session. Never throws.
	 */
	async checkSession(forceRenewToken?: boolean): Promise<string> {
		const tokens = this.getCurrentTokens();
		if (tokens === null) {
			return "";
		}

		try {
			const freshTokens = await this.refreshPKCESessionIfNeeded(tokens, forceRenewToken ?? false);

			this.currentAccessToken = freshTokens.accessToken;
			this.currentRefreshToken = freshTokens.refreshToken;
			// The refresh endpoint returns no id_token — preserve the one we already hold.
			if (freshTokens.idToken) {
				this.currentIdToken = freshTokens.idToken;
			}
			safeSetItem(APP_TOKENS_KEY, JSON.stringify({ ...freshTokens, idToken: this.currentIdToken ?? undefined }));
			return freshTokens.accessToken;
		} catch {
			return "";
		}
	}

	/** Current tokens for hooks that need to decode the JWT (e.g. AuthProvider). */
	getTokens(): Maybe<TokenSet> {
		return this.getCurrentTokens();
	}

	/** In-memory tokens if present, else parsed from localStorage; null when unauthenticated. */
	private getCurrentTokens(): Maybe<TokenSet> {
		if (this.currentAccessToken && this.currentRefreshToken) {
			return {
				accessToken: this.currentAccessToken,
				idToken: this.currentIdToken ?? undefined,
				refreshToken: this.currentRefreshToken
			};
		}

		const tokenStr = window.localStorage.getItem(APP_TOKENS_KEY);
		if (!tokenStr) {
			return null;
		}
		try {
			return JSON.parse(tokenStr) as TokenSet;
		} catch {
			logger.warn("Corrupted token data in localStorage, clearing appTokens");
			window.localStorage.removeItem(APP_TOKENS_KEY);
			return null;
		}
	}

	private async refreshPKCESessionIfNeeded(tokens: TokenSet, forceRenewToken: boolean): Promise<TokenSet> {
		if (forceRenewToken || this.isTokenNearExpiry(tokens.accessToken)) {
			return this.refreshToken(tokens.accessToken, tokens.refreshToken);
		}
		return { ...tokens };
	}

	private async refreshToken(accessToken: string, refreshToken: string): Promise<JwtRefreshResponse> {
		// FusionAuth JWT refresh (not oauth2/token).
		const response = await fetch(`${authUrl}/api/jwt/refresh`, {
			body: JSON.stringify({ refreshToken, token: accessToken }),
			headers: { "Content-Type": "application/json" },
			method: "POST"
		});

		if (!response.ok) {
			throw new Error(response.statusText);
		}

		const data = (await response.json()) as JwtRefreshApiResponse;
		return { accessToken: data.token, refreshToken: data.refreshToken };
	}

	/** Decode a JWT payload without verifying the signature (verification is the API's job). */
	parseTokenPayload(accessToken: string): SFJWT {
		const base64Url = accessToken.split(".")[1];
		const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
		const jsonPayload = decodeURIComponent(
			window
				.atob(base64)
				.split("")
				.map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
				.join("")
		);
		return JSON.parse(jsonPayload) as SFJWT;
	}

	/**
	 * True if the access token is within `thresholdPercentage` of expiry relative to its lifetime.
	 * E.g. for a 3600s token at 20%, near-expiry triggers with <= 720s remaining.
	 */
	isTokenNearExpiry(accessToken: string, thresholdPercentage = 20): boolean {
		const { exp, iat } = this.parseTokenPayload(accessToken);
		const currentTime = Date.now() / 1000;
		const timeRemaining = exp - currentTime;
		const tokenLifetime = exp - iat;
		const thresholdTime = (thresholdPercentage / 100) * tokenLifetime;
		return timeRemaining <= thresholdTime;
	}

	/** Exchange a PKCE authorization code for tokens and persist them. */
	async exchangePKCECodeForTokens(code: string): Promise<void> {
		const verifier = window.localStorage.getItem(PKCE_VERIFIER_KEY);
		if (verifier === null) {
			throw new Error("No PKCE verifier found.");
		}

		const data = new URLSearchParams();
		data.append("client_id", clientId);
		data.append("code", code);
		data.append("code_verifier", verifier);
		data.append("grant_type", "authorization_code");
		data.append("redirect_uri", `${window.location.origin}/oauth-redirect`);

		const response = await fetch(`${authUrl}/oauth2/token`, {
			body: data,
			headers: { "Content-Type": "application/x-www-form-urlencoded" },
			method: "POST"
		});

		if (response.status !== 200) {
			throw new Error("Error exchanging code for tokens");
		}

		const tokensResponse = (await response.json()) as OAuthTokenResponse;
		const tokens: TokenSet = {
			accessToken: tokensResponse.access_token,
			idToken: tokensResponse.id_token,
			refreshToken: tokensResponse.refresh_token
		};

		safeSetItem(APP_TOKENS_KEY, JSON.stringify(tokens));
		window.localStorage.removeItem(PKCE_VERIFIER_KEY);

		this.currentAccessToken = tokens.accessToken;
		this.currentIdToken = tokens.idToken ?? null;
		this.currentRefreshToken = tokens.refreshToken;
	}

	/** Build the OAuth2 authorize URL (S256 + CSRF-hashed state) and navigate to FusionAuth. */
	async redirectToLogin(onSuccessReturnToPath = true): Promise<void> {
		// Already returning from a login attempt — don't loop.
		if (window.location.pathname.startsWith("/oauth-redirect")) {
			return;
		}

		const authState = {
			// Optional dev aid: return to the originating port after auth.
			port: window.location.port,
			// By default return to the current path; otherwise to root.
			returnTo: onSuccessReturnToPath ? window.location.pathname : "/"
		};

		const { challenge, verifier } = await generateVerifierAndChallenge();
		safeSetItem(PKCE_VERIFIER_KEY, verifier);

		const stateStr = JSON.stringify(authState);
		// Store a SHA-256 hash of the state for CSRF validation on callback.
		const stateHash = await sha256Hex(stateStr);
		try {
			window.sessionStorage.setItem(OAUTH_STATE_HASH_KEY, stateHash);
		} catch {
			// sessionStorage full — proceed; the callback will treat a missing hash as invalid.
		}

		const params = new URLSearchParams({
			client_id: clientId,
			code_challenge: challenge,
			code_challenge_method: "S256",
			redirect_uri: `${window.location.origin}/oauth-redirect`,
			response_type: "code",
			scope: "openid profile email offline_access",
			state: stateStr
		});
		window.location.href = `${authUrl}/oauth2/authorize?${params.toString()}`;
	}

	/** Hygienic logout: null tokens, targeted storage removal, then navigate to FusionAuth logout. */
	redirectToLogout(): void {
		const params = new URLSearchParams({
			client_id: clientId,
			state: JSON.stringify({ port: window.location.port }),
			tenantId
		});
		const logoutUrl = `${authUrl}/oauth2/logout?${params.toString()}`;

		// Order matters: null in-memory tokens first, then remove session-bound storage. No Redux
		// LOGOUT dispatch — an RTK refetch races the storage clear and can silently re-auth via
		// checkSession(). Targeted removal (never localStorage.clear) preserves unrelated prefs.
		this.currentAccessToken = null;
		this.currentIdToken = null;
		this.currentRefreshToken = null;
		for (const key of SESSION_BOUND_LOCAL_STORAGE_KEYS) {
			window.localStorage.removeItem(key);
		}
		window.sessionStorage.removeItem(OAUTH_STATE_HASH_KEY);

		window.location.href = logoutUrl;
	}
}

const sfAuth = SFAuth.Instance;

export default sfAuth;
